import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface UploadItem {
  id: string;
  file: File;
  bucket: string;
  path: string;
  metadata?: Record<string, unknown>;
  progress: number;
  status: 'queued' | 'uploading' | 'completed' | 'failed';
  error?: string;
  url?: string;
}

interface UploadQueueState {
  items: UploadItem[];
}

type UploadAction =
  | { type: 'ADD_ITEM'; payload: UploadItem }
  | { type: 'START_UPLOAD'; payload: string }
  | { type: 'UPDATE_PROGRESS'; payload: { id: string; progress: number } }
  | { type: 'COMPLETE_ITEM'; payload: { id: string; url: string } }
  | { type: 'FAIL_ITEM'; payload: { id: string; error: string } }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'CLEAR_COMPLETED' };

const reducer = (state: UploadQueueState, action: UploadAction): UploadQueueState => {
  switch (action.type) {
    case 'ADD_ITEM':
      return { items: [...state.items, action.payload] };
    case 'START_UPLOAD':
      return { items: state.items.map(i => i.id === action.payload ? { ...i, status: 'uploading' as const } : i) };
    case 'UPDATE_PROGRESS':
      return { items: state.items.map(i => i.id === action.payload.id ? { ...i, progress: action.payload.progress } : i) };
    case 'COMPLETE_ITEM':
      return { items: state.items.map(i => i.id === action.payload.id ? { ...i, status: 'completed' as const, progress: 100, url: action.payload.url } : i) };
    case 'FAIL_ITEM':
      return { items: state.items.map(i => i.id === action.payload.id ? { ...i, status: 'failed' as const, error: action.payload.error } : i) };
    case 'REMOVE_ITEM':
      return { items: state.items.filter(i => i.id !== action.payload) };
    case 'CLEAR_COMPLETED':
      return { items: state.items.filter(i => i.status !== 'completed') };
    default:
      return state;
  }
};

interface UploadQueueContextType {
  state: UploadQueueState;
  addToQueue: (file: File, bucket: string, path: string, metadata?: Record<string, unknown>) => string;
  removeFromQueue: (id: string) => void;
  clearCompleted: () => void;
  retryUpload: (id: string) => void;
  totalProgress: number;
  hasActiveUploads: boolean;
}

const UploadQueueContext = createContext<UploadQueueContextType | null>(null);

const MAX_CONCURRENT = 3;

export const UploadQueueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, { items: [] });
  const uploadingCountRef = useRef(0);

  const processUpload = useCallback(async (item: UploadItem) => {
    dispatch({ type: 'START_UPLOAD', payload: item.id });
    uploadingCountRef.current++;

    try {
      const { data, error } = await supabase.storage
        .from(item.bucket)
        .upload(item.path, item.file, { upsert: true });

      if (error) throw error;

      const { data: urlData } = supabase.storage.from(item.bucket).getPublicUrl(data.path);
      dispatch({ type: 'COMPLETE_ITEM', payload: { id: item.id, url: urlData.publicUrl } });
    } catch (err: any) {
      dispatch({ type: 'FAIL_ITEM', payload: { id: item.id, error: err.message || 'Upload failed' } });
    } finally {
      uploadingCountRef.current--;
    }
  }, []);

  useEffect(() => {
    const queued = state.items.filter(i => i.status === 'queued');
    const available = MAX_CONCURRENT - uploadingCountRef.current;

    queued.slice(0, available).forEach(item => {
      processUpload(item);
    });
  }, [state.items, processUpload]);

  const addToQueue = useCallback((file: File, bucket: string, path: string, metadata?: Record<string, unknown>) => {
    const id = `upload_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const item: UploadItem = { id, file, bucket, path, metadata, progress: 0, status: 'queued' };
    dispatch({ type: 'ADD_ITEM', payload: item });
    return id;
  }, []);

  const removeFromQueue = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: id });
  }, []);

  const clearCompleted = useCallback(() => {
    dispatch({ type: 'CLEAR_COMPLETED' });
  }, []);

  const retryUpload = useCallback((id: string) => {
    const item = state.items.find(i => i.id === id);
    if (item && item.status === 'failed') {
      dispatch({ type: 'REMOVE_ITEM', payload: id });
      const newId = `upload_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      dispatch({ type: 'ADD_ITEM', payload: { ...item, id: newId, progress: 0, status: 'queued', error: undefined } });
    }
  }, [state.items]);

  const activeItems = state.items.filter(i => i.status === 'uploading' || i.status === 'queued');
  const totalProgress = activeItems.length > 0
    ? activeItems.reduce((sum, i) => sum + i.progress, 0) / activeItems.length
    : 0;
  const hasActiveUploads = activeItems.length > 0;

  return (
    <UploadQueueContext.Provider value={{ state, addToQueue, removeFromQueue, clearCompleted, retryUpload, totalProgress, hasActiveUploads }}>
      {children}
    </UploadQueueContext.Provider>
  );
};

export const useUploadQueue = () => {
  const context = useContext(UploadQueueContext);
  if (!context) throw new Error('useUploadQueue must be used within UploadQueueProvider');
  return context;
};
